import React from "react";

/**
 * <EventInfoFieldset /> — reusable form section for the host-supplied
 * logistics + insurance metadata. Used by HostCreateEvent, the host-edit
 * panel inside HostEventDetail, and the admin edit-event modal.
 *
 * Props:
 *   value: the current event_info dict (may be undefined — falls back to {})
 *   onChange(next): receives the updated event_info dict on every keystroke
 *   className: optional wrapper class for layout overrides
 *   testidPrefix: data-testid base, e.g. "host-event-info" so child fields
 *                 become "host-event-info-medical", "host-event-info-wifi", …
 *
 * Design: optional throughout. Hosts can leave any subset blank. Empty
 * string is sent as null when serialized so backend stores cleanly.
 */
export function EventInfoFieldset({ value, onChange, className = "", testidPrefix = "event-info" }) {
    const v = value || {};
    const set = (key, val) => onChange({ ...v, [key]: val });

    const yesNo = (key, label, hint) => (
        <div className="space-y-1.5">
            <label className="label-base">{label}</label>
            <div className="inline-flex rounded-full border border-brand-line overflow-hidden text-sm" data-testid={`${testidPrefix}-${key}`}>
                {[
                    { val: true,  txt: "Yes" },
                    { val: false, txt: "No" },
                    { val: null,  txt: "—" },
                ].map((opt) => {
                    const isActive = v[key] === opt.val || (v[key] == null && opt.val === null);
                    return (
                        <button
                            type="button"
                            key={String(opt.txt)}
                            data-testid={`${testidPrefix}-${key}-${opt.txt.toLowerCase()}`}
                            onClick={() => set(key, opt.val)}
                            className={`px-3 py-1.5 font-semibold ${isActive ? "bg-brand-forest text-white" : "bg-white text-brand-mute hover:bg-brand-bone"}`}
                        >
                            {opt.txt}
                        </button>
                    );
                })}
            </div>
            {hint && <div className="text-xs text-brand-mute">{hint}</div>}
        </div>
    );

    return (
        <div className={`space-y-8 ${className}`}>
            {/* Insurance carriers */}
            <section>
                <h3 className="font-heading font-bold">Insurance information</h3>
                <p className="text-xs text-brand-mute mt-0.5">Helps vendors know which networks employees are on so they can plan in-network options.</p>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                    <label className="space-y-1.5 block">
                        <span className="label-base">Medical</span>
                        <input
                            data-testid={`${testidPrefix}-medical`}
                            className="input-base"
                            placeholder="e.g. Aetna PPO"
                            value={v.medical_insurance || ""}
                            onChange={(e) => set("medical_insurance", e.target.value)}
                        />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="label-base">Dental</span>
                        <input
                            data-testid={`${testidPrefix}-dental`}
                            className="input-base"
                            placeholder="e.g. MetLife"
                            value={v.dental_insurance || ""}
                            onChange={(e) => set("dental_insurance", e.target.value)}
                        />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="label-base">Vision</span>
                        <input
                            data-testid={`${testidPrefix}-vision`}
                            className="input-base"
                            placeholder="e.g. VSP"
                            value={v.vision_insurance || ""}
                            onChange={(e) => set("vision_insurance", e.target.value)}
                        />
                    </label>
                </div>
            </section>

            {/* Workforce composition */}
            <section>
                <h3 className="font-heading font-bold">Workforce composition</h3>
                <p className="text-xs text-brand-mute mt-0.5">Optional. Lets vendors tailor giveaways and screenings.</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <label className="space-y-1.5 block">
                        <span className="label-base">% male employees</span>
                        <input
                            data-testid={`${testidPrefix}-male-pct`}
                            className="input-base"
                            type="number" min="0" max="100"
                            placeholder="e.g. 45"
                            value={v.male_employees_pct ?? ""}
                            onChange={(e) => set("male_employees_pct", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                        />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="label-base">% female employees</span>
                        <input
                            data-testid={`${testidPrefix}-female-pct`}
                            className="input-base"
                            type="number" min="0" max="100"
                            placeholder="e.g. 55"
                            value={v.female_employees_pct ?? ""}
                            onChange={(e) => set("female_employees_pct", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                        />
                    </label>
                </div>
            </section>

            {/* Venue logistics */}
            <section>
                <h3 className="font-heading font-bold">Venue logistics</h3>
                <p className="text-xs text-brand-mute mt-0.5">What vendors can expect on-site.</p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 mt-4">
                    {yesNo("wifi", "Wi-Fi available?")}
                    {yesNo("electricity", "Electricity at booth?")}
                    {yesNo("tables_chairs_provided", "Tables & chairs provided?")}
                    <div className="space-y-1.5">
                        <label className="label-base">Venue type</label>
                        <div className="inline-flex rounded-full border border-brand-line overflow-hidden text-sm" data-testid={`${testidPrefix}-venue-type`}>
                            {[
                                { val: "indoor",  txt: "Indoor" },
                                { val: "outdoor", txt: "Outdoor" },
                                { val: "both",    txt: "Both" },
                                { val: null,      txt: "—" },
                            ].map((opt) => {
                                const isActive = v.venue_type === opt.val || (!v.venue_type && opt.val === null);
                                return (
                                    <button
                                        type="button"
                                        key={String(opt.txt)}
                                        data-testid={`${testidPrefix}-venue-type-${(opt.val || "none").toLowerCase()}`}
                                        onClick={() => set("venue_type", opt.val)}
                                        className={`px-3 py-1.5 font-semibold ${isActive ? "bg-brand-forest text-white" : "bg-white text-brand-mute hover:bg-brand-bone"}`}
                                    >
                                        {opt.txt}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <label className="block mt-5 space-y-1.5">
                    <span className="label-base">Tables/chairs details (optional)</span>
                    <textarea
                        data-testid={`${testidPrefix}-tables-chairs-notes`}
                        className="input-base !h-auto py-2"
                        rows={2}
                        placeholder='e.g. "One 6ft table and at least one chair per vendor"'
                        value={v.tables_chairs_notes || ""}
                        onChange={(e) => set("tables_chairs_notes", e.target.value)}
                    />
                </label>
                <div className="mt-5">
                    {yesNo("catered", "Event catered?", "Whether food/beverages are provided to attendees and vendors.")}
                </div>
            </section>
        </div>
    );
}

/**
 * <EventInfoDisplay /> — read-only pretty render of event_info for the
 * public event detail page. Hides empty fields cleanly.
 *
 * `totalEmployees` (optional) gets folded into the Workforce pod so the
 * three demographic data points (count + male % + female %) sit together
 * instead of total_employees living in a separate top-tile.
 *
 * `gated` (optional, default false) — when true (unauthenticated visitors),
 * any pod that WOULD have rendered data is replaced with a "details after
 * login" teaser. Pods that have no data at all still don't render.
 */
export function EventInfoDisplay({ value, totalEmployees, gated = false }) {
    const v = value || {};
    const hasInsurance = v.medical_insurance || v.dental_insurance || v.vision_insurance;
    const hasWorkforce = (
        v.male_employees_pct != null
        || v.female_employees_pct != null
        || (totalEmployees != null && totalEmployees !== "")
    );
    const hasLogistics = (
        v.wifi != null || v.electricity != null || v.tables_chairs_provided != null
        || v.venue_type || v.tables_chairs_notes || v.catered != null
    );
    if (!hasInsurance && !hasWorkforce && !hasLogistics) return null;

    const yesNoLabel = (b) => (b === true ? "Yes" : b === false ? "No" : null);
    const Row = ({ label, value: val }) => {
        if (val == null || val === "") return null;
        return (
            <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm border-b border-brand-line/60 last:border-b-0">
                <span className="text-brand-mute">{label}</span>
                <span className="font-semibold text-brand-ink text-right break-words">{val}</span>
            </div>
        );
    };

    // Used when `gated` and a pod has data — vendors must sign in to see
    // exact carrier names, head counts, and logistics specifics. Hides
    // employer-sensitive info behind the auth wall without removing the
    // signal that this event HAS that info.
    const LockedPod = ({ title }) => (
        <div className="rounded-xl border border-dashed border-brand-line bg-white p-5 flex flex-col" data-testid={`event-info-locked-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="font-heading font-bold mb-2">{title}</div>
            <div className="text-sm text-brand-mute leading-snug">
                Provided by the host — <span className="text-brand-ink font-semibold">exact details after login</span>.
            </div>
            <a href="/login" className="text-xs font-semibold text-brand-coral mt-3 hover:underline self-start">Sign in to view →</a>
        </div>
    );

    return (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="event-info-display">
            {hasInsurance && (gated
                ? <LockedPod title="Insurance carriers" />
                : (
                    <div className="rounded-xl border border-brand-line bg-white p-5">
                        <div className="font-heading font-bold mb-2">Insurance carriers</div>
                        <Row label="Medical" value={v.medical_insurance} />
                        <Row label="Dental"  value={v.dental_insurance} />
                        <Row label="Vision"  value={v.vision_insurance} />
                    </div>
                )
            )}
            {hasWorkforce && (gated
                ? <LockedPod title="Workforce" />
                : (
                    <div className="rounded-xl border border-brand-line bg-white p-5">
                        <div className="font-heading font-bold mb-2">Workforce</div>
                        <Row label="Total employees" value={(totalEmployees != null && totalEmployees !== "") ? Number(totalEmployees).toLocaleString() : null} />
                        <Row label="% male"   value={v.male_employees_pct != null ? `${v.male_employees_pct}%` : null} />
                        <Row label="% female" value={v.female_employees_pct != null ? `${v.female_employees_pct}%` : null} />
                    </div>
                )
            )}
            {hasLogistics && (gated
                ? <LockedPod title="Venue logistics" />
                : (
                    <div className="rounded-xl border border-brand-line bg-white p-5">
                        <div className="font-heading font-bold mb-2">Venue logistics</div>
                        <Row label="Venue type" value={v.venue_type ? v.venue_type[0].toUpperCase() + v.venue_type.slice(1) : null} />
                        <Row label="Wi-Fi" value={yesNoLabel(v.wifi)} />
                        <Row label="Electricity" value={yesNoLabel(v.electricity)} />
                        <Row label="Tables/chairs provided" value={yesNoLabel(v.tables_chairs_provided)} />
                        <Row label="Details" value={v.tables_chairs_notes} />
                        <Row label="Catered" value={yesNoLabel(v.catered)} />
                    </div>
                )
            )}
        </div>
    );
}
